import { formatMoney, formatDateTime, maskPhone } from './transactionDetail';

/** Logo Flow en version texte blanc, posé sur l'en-tête sombre du reçu. */
const FLOW_LOGO_LIGHT = '/images/Logo_Netikash%20Flow%20Color.png';
/** Symbole Flow seul, pour le pied de page. */
const FLOW_MARK = '/images/Logo_Netikash%20Flow%20-%20Logo%20Only%20512x512.png';

const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const STATUS_STYLES = {
    success: { fg: '#047857', bg: '#ECFDF5', border: '#A7F3D0', icon: '&#10003;' },
    pending: { fg: '#B45309', bg: '#FFFBEB', border: '#FDE68A', icon: '&#8987;' },
    failed: { fg: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', icon: '&#10005;' },
};

const splitAmount = (amount, currency) => {
    const n = Number(amount || 0);
    const whole = Math.trunc(n).toLocaleString('fr-FR');
    const cents = Math.round((Math.abs(n) % 1) * 100);
    return { whole, cents: cents ? `,${String(cents).padStart(2, '0')}` : '', currency };
};

/** Initiales du marchand (2 lettres max) pour son monogramme. */
const initials = (name) =>
    String(name || 'M').trim().split(/\s+/).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('') || 'M';

/**
 * Reçu client (HTML autonome, prêt à imprimer ou enregistrer en PDF) :
 * pas de frais marchand, numéro du payeur masqué, référence mise en avant pour les réclamations.
 */
export const buildReceiptHtml = (detail, organization) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const status = STATUS_STYLES[detail.statusTone] || STATUS_STYLES.pending;
    const { whole, cents, currency } = splitAmount(detail.amount, detail.currency);
    const merchantName = organization?.name || 'Marchand';
    const paidAt = detail.completedAt || detail.requestedAt;
    const merchantSub = [detail.terminal ? `Terminal ${detail.terminal}` : detail.channel, formatDateTime(paidAt)]
        .filter(Boolean).join(' · ');

    const w = detail.isWithdrawal;
    const texts = w
        ? {
            docType: 'Reçu de retrait',
            amountLabel: 'Montant retiré',
            thanks: 'Retrait enregistré',
            note: `Retrait effectué depuis le compte Flow de ${merchantName}.`,
            hint: 'Communiquez cette référence au support pour tout suivi de ce retrait.',
        }
        : {
            docType: 'Reçu de paiement',
            amountLabel: 'Montant payé',
            thanks: 'Merci pour votre paiement',
            note: `Conservez ce reçu : il fait foi de votre paiement auprès de ${merchantName}.`,
            hint: 'Communiquez cette référence au marchand ou au support en cas de réclamation.',
        };
    const rows = [
        ['Date et heure', formatDateTime(paidAt)],
        ['Canal', detail.channel],
        [w ? 'Moyen de retrait' : 'Moyen de paiement', detail.method],
        ['Opérateur', detail.provider],
        [w ? 'Bénéficiaire' : 'Payeur', detail.payerName && detail.payerName !== detail.payerPhone ? detail.payerName : null],
        [w ? 'Numéro du bénéficiaire' : 'Numéro du payeur', detail.payerPhone ? maskPhone(detail.payerPhone) : null],
        // Retrait : reçu du marchand lui-même, les frais le concernent
        ['Frais', w ? formatMoney(detail.fees, detail.currency) : null],
        ['Net versé', w ? formatMoney(detail.netAmount, detail.currency) : null],
        ['Motif', detail.label],
    ].filter(([, v]) => v);

    return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(texts.docType)} ${escapeHtml(detail.reference)} - ${escapeHtml(merchantName)}</title>
<style>
  @page { size: A5 portrait; margin: 10mm; }
  :root { color-scheme: light; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; padding: 40px 16px; background: #E9EDF2; color: #0F172A;
         font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', Arial, sans-serif; }
  .ticket { position: relative; max-width: 440px; margin: 0 auto; background: #FFFFFF; border-radius: 22px;
            overflow: hidden; box-shadow: 0 24px 60px -20px rgba(15, 23, 42, .35); }

  /* En-tête sombre : logo Flow + marchand */
  .band { position: relative; padding: 24px 26px 26px; color: #fff;
          background: radial-gradient(120% 140% at 100% 0%, rgba(234, 179, 8, .26) 0%, rgba(234, 179, 8, 0) 55%),
                      linear-gradient(135deg, #0B1220 0%, #1E293B 100%);
          border-bottom: 4px solid #EAB308; }
  .band-top { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .band-top img { height: 36px; display: block; }
  .doc-type { font-size: 10px; font-weight: 700; letter-spacing: .24em; text-transform: uppercase; color: #FACC15;
              padding: 6px 10px; border: 1px solid rgba(250, 204, 21, .45); border-radius: 999px; }
  .merchant { margin-top: 22px; display: flex; align-items: center; gap: 14px; }
  .monogram { width: 46px; height: 46px; flex-shrink: 0; border-radius: 14px; display: flex; align-items: center; justify-content: center;
              font-size: 17px; font-weight: 800; color: #1E293B; background: linear-gradient(135deg, #FDE68A, #EAB308); }
  .merchant-name { font-size: 18px; font-weight: 700; line-height: 1.2; }
  .merchant-sub { margin-top: 4px; font-size: 12px; color: #CBD5E1; }

  /* Montant */
  .amount { padding: 28px 28px 20px; text-align: center; }
  .amount-label { font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: #94A3B8; }
  .amount-value { margin-top: 8px; font-size: 44px; font-weight: 800; letter-spacing: -.03em; line-height: 1; }
  .amount-value small { font-size: 22px; font-weight: 700; }
  .amount-value em { font-style: normal; font-size: 18px; font-weight: 700; color: #64748B; margin-left: 6px; letter-spacing: 0; }
  .status { display: inline-flex; align-items: center; gap: 8px; margin-top: 14px; padding: 6px 14px 6px 6px; border-radius: 999px;
            font-size: 13px; font-weight: 700; color: ${status.fg}; background: ${status.bg}; border: 1px solid ${status.border}; }
  .status i { font-style: normal; width: 22px; height: 22px; border-radius: 999px; display: inline-flex; align-items: center;
              justify-content: center; color: #fff; background: ${status.fg}; font-size: 12px; }

  /* Perforation */
  .tear { position: relative; height: 22px; margin: 4px 0; }
  .tear::before { content: ''; position: absolute; left: 22px; right: 22px; top: 50%; border-top: 2px dashed #E2E8F0; }
  .tear span { position: absolute; top: 0; width: 22px; height: 22px; border-radius: 999px; background: #E9EDF2; }
  .tear span:first-child { left: -11px; } .tear span:last-child { right: -11px; }

  /* Référence */
  .ref { margin: 8px 22px 4px; padding: 16px 18px; text-align: center; border-radius: 16px;
         background: linear-gradient(135deg, #FFFBEB, #FEF3C7); border: 1px dashed #EAB308; }
  .ref-label { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; color: #92400E; font-weight: 700; }
  .ref-code { margin-top: 8px; font-family: 'Cascadia Mono', Consolas, 'SFMono-Regular', monospace; font-size: 22px; font-weight: 800;
              letter-spacing: .08em; color: #78350F; word-break: break-all; }
  .ref-hint { margin-top: 8px; font-size: 11px; color: #A16207; line-height: 1.4; }

  /* Détails */
  .details { margin: 14px 22px 0; }
  .row { display: flex; justify-content: space-between; gap: 18px; padding: 11px 2px; border-bottom: 1px solid #F1F5F9; font-size: 13px; }
  .row:last-child { border-bottom: 0; }
  .row dt { color: #64748B; }
  .row dd { margin: 0; text-align: right; font-weight: 600; color: #0F172A; word-break: break-word; }
  .receipt-id { margin: 6px 24px 0; font-size: 10px; color: #94A3B8; word-break: break-all; }

  /* Pied */
  .foot { margin-top: 20px; padding: 18px 24px 22px; background: #F8FAFC; border-top: 1px solid #F1F5F9; text-align: center; }
  .thanks { font-size: 14px; font-weight: 700; }
  .note { margin-top: 6px; font-size: 11px; color: #64748B; line-height: 1.5; }
  .brand { margin-top: 14px; display: inline-flex; align-items: center; gap: 6px; font-size: 10px; color: #94A3B8; letter-spacing: .04em; }
  .brand img { height: 14px; }

  @media print {
    body { background: #fff; padding: 0; }
    .ticket { box-shadow: none; border: 1px solid #E2E8F0; }
    .tear span { background: #fff; }
  }
</style>
</head>
<body>
  <article class="ticket">
    <header class="band">
      <div class="band-top">
        <img src="${origin}${FLOW_LOGO_LIGHT}" alt="Flow by Netikash" />
        <span class="doc-type">${escapeHtml(texts.docType)}</span>
      </div>
      <div class="merchant">
        <div class="monogram">${escapeHtml(initials(merchantName))}</div>
        <div>
          <div class="merchant-name">${escapeHtml(merchantName)}</div>
          <div class="merchant-sub">${escapeHtml(merchantSub)}</div>
        </div>
      </div>
    </header>

    <section class="amount">
      <div class="amount-label">${escapeHtml(texts.amountLabel)}</div>
      <div class="amount-value">${escapeHtml(whole)}<small>${escapeHtml(cents)}</small><em>${escapeHtml(currency)}</em></div>
      <div class="status"><i>${status.icon}</i>${escapeHtml(detail.statusLabel)}</div>
    </section>

    <div class="tear" aria-hidden="true"><span></span><span></span></div>

    <section class="ref">
      <div class="ref-label">Référence de la transaction</div>
      <div class="ref-code">${escapeHtml(detail.reference)}</div>
      <div class="ref-hint">${escapeHtml(texts.hint)}</div>
    </section>

    <dl class="details">
      ${rows.map(([k, v]) => `<div class="row"><dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd></div>`).join('')}
    </dl>
    ${detail.receiptId ? `<p class="receipt-id">ID de transaction : ${escapeHtml(detail.receiptId)}</p>` : ''}

    <footer class="foot">
      <div class="thanks">${escapeHtml(texts.thanks)}</div>
      <div class="note">${escapeHtml(texts.note)}</div>
      <div class="brand"><img src="${origin}${FLOW_MARK}" alt="" /> Reçu émis par Flow by Netikash · ${escapeHtml(formatDateTime(new Date()))}</div>
    </footer>
  </article>
</body>
</html>`;
};

/**
 * Ouvre la boîte d'impression du navigateur sur le reçu (« Enregistrer au format PDF » pour le télécharger).
 * Un iframe caché évite le blocage des pop-ups ; on attend les images (logos) avant d'imprimer.
 */
export const printReceipt = (detail, organization) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = win.document;
    doc.open();
    doc.write(buildReceiptHtml(detail, organization));
    doc.close();

    let printed = false;
    const print = () => {
        if (printed) return;
        printed = true;
        win.focus();
        win.print();
        setTimeout(() => iframe.remove(), 1000);
    };
    const images = Array.from(doc.images);
    const pending = images.filter((img) => !img.complete);
    if (pending.length === 0) {
        setTimeout(print, 50);
        return;
    }
    let remaining = pending.length;
    const done = () => { remaining -= 1; if (remaining <= 0) print(); };
    pending.forEach((img) => { img.addEventListener('load', done); img.addEventListener('error', done); });
    setTimeout(print, 3000); // filet si un logo ne répond pas
};

/** Texte partagé : l'essentiel pour une réclamation, sans données sensibles. */
export const buildShareText = (detail, organization) => [
    `${detail.isWithdrawal ? 'Retrait' : 'Reçu'} ${organization?.name ? `${organization.name} ` : ''}— Flow by Netikash`,
    `Référence : ${detail.reference}`,
    `Montant : ${formatMoney(detail.amount, detail.currency)}`,
    `Statut : ${detail.statusLabel}`,
    `Date : ${formatDateTime(detail.completedAt || detail.requestedAt)}`,
].join('\n');

/** Partage natif (mobile) si disponible, sinon copie dans le presse-papiers. Renvoie 'shared' | 'copied' | 'cancelled'. */
export const shareTransaction = async (detail, organization) => {
    const text = buildShareText(detail, organization);
    if (navigator.share) {
        try {
            await navigator.share({ title: `Reçu ${detail.reference}`, text });
            return 'shared';
        } catch (error) {
            if (error?.name === 'AbortError') return 'cancelled';
        }
    }
    await navigator.clipboard.writeText(text);
    return 'copied';
};
